import React, { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Divider } from "primereact/divider";
import { adminApi } from "../../../api/adminApi";
import { useAuth } from "../../../context/AuthContext";

const LABELS = {
  "alta-autoexcluido": {
    titulo: "Alta de Autoexcluido",
    descripcion: "Estas personas reciben un email cada vez que se registra un nuevo autoexcluido.",
    icono: "pi-user-plus",
  },
  "intento-ingreso": {
    titulo: "Alerta de Intento de Ingreso",
    descripcion: "Estas personas reciben una alerta cuando un autoexcluido intenta ingresar a una sala.",
    icono: "pi-exclamation-triangle",
  },
};

function SeccionNotificacion({ config, onActualizar, esSuperadmin }) {
  const toast = useRef(null);
  const [destinatarios, setDestinatarios] = useState(config.destinatarios || []);
  const [nuevoEmail, setNuevoEmail] = useState("");
  const [nuevoNombre, setNuevoNombre] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [importando, setImportando] = useState(false);
  const meta = LABELS[config.id] || { titulo: config.id, descripcion: "", icono: "pi-bell" };

  async function agregar() {
    if (!nuevoEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nuevoEmail)) {
      toast.current?.show({ severity: "warn", summary: "Email inválido", detail: "Ingresá un email válido" });
      return;
    }
    if (destinatarios.some((d) => d.email === nuevoEmail)) {
      toast.current?.show({ severity: "warn", summary: "Ya existe", detail: "Ese email ya está en la lista" });
      return;
    }
    const nuevos = [...destinatarios, { email: nuevoEmail, nombre: nuevoNombre || nuevoEmail }];
    await guardar(nuevos);
    setNuevoEmail("");
    setNuevoNombre("");
  }

  function confirmarQuitar(email) {
    confirmDialog({
      message: `¿Quitar ${email} de las notificaciones de "${meta.titulo}"?`,
      header: "Quitar destinatario",
      icon: "pi pi-trash",
      acceptClassName: "p-button-danger",
      acceptLabel: "Quitar",
      rejectLabel: "Cancelar",
      accept: () => quitar(email),
    });
  }

  async function quitar(email) {
    const nuevos = destinatarios.filter((d) => d.email !== email);
    await guardar(nuevos);
  }

  async function guardar(nuevos) {
    setGuardando(true);
    try {
      await adminApi.actualizarNotificacion(config.id, nuevos);
      setDestinatarios(nuevos);
      onActualizar?.(config.id, nuevos);
      toast.current?.show({ severity: "success", summary: "Guardado", detail: "Lista de destinatarios actualizada" });
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "No se pudo actualizar la lista" });
    } finally {
      setGuardando(false);
    }
  }

  function confirmarImportar() {
    confirmDialog({
      message: `Se importarán los emails de los usuarios con rol admin, adminauto y agente como destinatarios de "${meta.titulo}". Los que ya existan no se duplican. ¿Continuar?`,
      header: "Importar destinatarios desde roles",
      icon: "pi pi-download",
      acceptClassName: "p-button-primary",
      acceptLabel: "Importar",
      rejectLabel: "Cancelar",
      accept: importarDeRoles,
    });
  }

  async function importarDeRoles() {
    setImportando(true);
    try {
      const { data } = await adminApi.importarDestinatariosDeRoles(config.id);
      setDestinatarios(data.destinatarios || []);
      onActualizar?.(config.id, data.destinatarios || []);
      toast.current?.show({
        severity: "success",
        summary: "Importado",
        detail: `${data.cantidadAgregados} destinatario(s) agregado(s) desde roles.`,
      });
    } catch (e) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: e.response?.data?.message || "No se pudo importar la lista",
      });
    } finally {
      setImportando(false);
    }
  }

  return (
    <div className="surface-card border-round border-1 surface-border p-4 mb-4">
      <Toast ref={toast} />
      <div className="flex align-items-center gap-2 mb-1">
        <i className={`pi ${meta.icono} text-xl text-primary`} />
        <h3 className="m-0">{meta.titulo}</h3>
      </div>
      <p className="text-sm text-500 mt-1 mb-3">{meta.descripcion}</p>

      {/* Lista de destinatarios actuales */}
      {destinatarios.length === 0 ? (
        <p className="text-sm text-400 mb-3">Sin destinatarios configurados.</p>
      ) : (
        <div className="flex flex-wrap gap-2 mb-3">
          {destinatarios.map((d) => (
            <div key={d.email} className="flex align-items-center gap-1 surface-hover border-round px-2 py-1">
              <i className="pi pi-envelope text-xs text-500" />
              <span className="text-sm">{d.nombre !== d.email ? `${d.nombre} <${d.email}>` : d.email}</span>
              <Button
                icon="pi pi-times"
                rounded
                text
                severity="danger"
                size="small"
                style={{ width: "20px", height: "20px" }}
                onClick={() => confirmarQuitar(d.email)}
                disabled={guardando}
              />
            </div>
          ))}
        </div>
      )}

      <Divider />

      {/* Agregar destinatario */}
      <p className="text-sm font-medium mb-2">Agregar destinatario</p>
      <div className="flex gap-2 flex-wrap align-items-center">
        <InputText
          value={nuevoEmail}
          onChange={(e) => setNuevoEmail(e.target.value)}
          placeholder="email@ejemplo.com"
          style={{ width: "220px" }}
          onKeyDown={(e) => e.key === "Enter" && agregar()}
        />
        <InputText
          value={nuevoNombre}
          onChange={(e) => setNuevoNombre(e.target.value)}
          placeholder="Nombre (opcional)"
          style={{ width: "180px" }}
        />
        <Button
          label="Agregar"
          icon="pi pi-plus"
          onClick={agregar}
          loading={guardando}
          size="small"
        />

        {esSuperadmin && (
          <Button
            label="Importar desde roles"
            icon="pi pi-download"
            onClick={confirmarImportar}
            loading={importando}
            severity="secondary"
            outlined
            size="small"
            tooltip="Importa los emails de usuarios con rol admin, adminauto y agente (solo superadmin)"
            tooltipOptions={{ position: "top" }}
          />
        )}
      </div>
    </div>
  );
}

export default function PanelNotificaciones() {
  const [configs, setConfigs] = useState([]);
  const [cargando, setCargando] = useState(true);
  const toast = useRef(null);
  const { esSuperadmin } = useAuth();

  useEffect(() => {
    adminApi.getNotificaciones()
      .then((res) => setConfigs(res.data))
      .catch(() => toast.current?.show({ severity: "error", summary: "Error", detail: "No se pudo cargar la configuración" }))
      .finally(() => setCargando(false));
  }, []);

  if (cargando) return <div className="p-4 text-center"><i className="pi pi-spin pi-spinner text-3xl" /></div>;

  return (
    <div className="p-3">
      <Toast ref={toast} />
      <ConfirmDialog />
      <h2 className="mb-1">Notificaciones de Autoexcluidos</h2>
      <p className="text-500 text-sm mb-4">Configurá quién recibe cada tipo de alerta por email.</p>
      {configs.map((config) => (
        <SeccionNotificacion key={config.id} config={config} esSuperadmin={esSuperadmin} />
      ))}
    </div>
  );
}
